import { z } from 'zod';
import { option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { RateGroupAddParameters, RateGroupUpdateParameters } from '@mews-commander/data-access';
import { Validation } from '@mews/framework';
import { WithRateGroupId, WithServiceId } from '@mews-commander/types';
import { DetailsFieldName } from '../components/forms/types';
import { paymentPolicySchema } from './paymentPolicySchema';
import { settlementConfigurationSchema } from './settlementConfigurationSchema';

interface WithDefaultLanguageCode {
    defaultLanguageCode: string;
}

export const createRateGroupDetailsSchema = ({ defaultLanguageCode }: WithDefaultLanguageCode) =>
    z.object({
        [DetailsFieldName.Names]: z
            .object({ [defaultLanguageCode]: Validation.String.Required.max(200) })
            .passthrough(),
        [DetailsFieldName.ShortNames]: z.record(z.string().max(70).nullish()).nullish(),
        [DetailsFieldName.Descriptions]: z.record(z.string().nullish()).nullish(),
        [DetailsFieldName.Ordering]: Validation.Number.MaxInteger.default(0).nullish(),
        [DetailsFieldName.RateGroupKey]: z.string().max(70).nullish(),
    });

export type RateGroupDetailsFormValues = z.infer<ReturnType<typeof createRateGroupDetailsSchema>>;

export const createRateGroupSchema = ({ defaultLanguageCode }: WithDefaultLanguageCode) =>
    createRateGroupDetailsSchema({ defaultLanguageCode })
        .merge(settlementConfigurationSchema)
        .merge(paymentPolicySchema);

export type RateGroupFormValues = z.infer<ReturnType<typeof createRateGroupSchema>>;

export const toRateGroupAddParameters =
    ({ serviceId }: WithServiceId) =>
    // @ts-expect-error, incorrect OpenAPI schema definition where settlementRules is required
    (formValues: RateGroupFormValues): RateGroupAddParameters => ({
        serviceId,
        names: formValues.names,
        shortNames: formValues.shortNames ? pipe(formValues.shortNames, record.filterMap(option.fromNullable)) : {},
        externalIdentifier: formValues.externalIdentifier,
        descriptions: formValues.descriptions
            ? pipe(formValues.descriptions, record.filterMap(option.fromNullable))
            : {},
        ordering: formValues.ordering,
        settlementMaximumTimeUnits: formValues.maximumTime || null,
        settlementExtent: formValues.extent,
        options: formValues.options ?? [],
        settlementRuleIds: formValues.settlementRules ?? [],
        cancellationPolicyIds: formValues.cancellationFees ?? [],
        settlementCurrencyCode: formValues.currencyCode,
    });

export const toRateGroupUpdateParameters =
    ({ rateGroupId }: WithRateGroupId) =>
    (formValues: RateGroupDetailsFormValues): RateGroupUpdateParameters => ({
        rateGroupId,
        externalIdentifier: { value: formValues.externalIdentifier },
        names: { value: formValues.names },
        shortNames: {
            value: formValues.shortNames ? pipe(formValues.shortNames, record.filterMap(option.fromNullable)) : {},
        },
        descriptions: {
            value: formValues.descriptions ? pipe(formValues.descriptions, record.filterMap(option.fromNullable)) : {},
        },
        ordering: { value: formValues.ordering ?? undefined },
    });
